import { useEffect, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import statisticsAPIService, { type PriorityHighSchoolStats } from "@/lib/statistics-api.service";
import { toAPIFilter } from "./utils";
import type { StatisticsFilters } from "./StatisticsControls";

interface PrioritySchoolComparisonProps {
	filters?: StatisticsFilters;
	/** Tăng lên mỗi lần trang yêu cầu tải lại. */
	refreshToken?: number;
}

const formatCount = (n: number) => n.toLocaleString("vi-VN");

/**
 * So tỉ lệ tiếp nhận giữa nhóm đến từ 149 trường THPT được ĐHQG-HCM ưu tiên và
 * phần còn lại.
 *
 * Danh sách 149 trường chỉ có nghĩa với khoá 2026 — khoá 2025 dùng hệ mã trường
 * khác, cùng con số nhưng trỏ sang tỉnh khác. Server từ chối tính cho khoá khác
 * thay vì trả số sai. Ở đây điều đó KHÔNG được hiển thị như một sự cố: chọn
 * khoá 2025 là thao tác hợp lệ, nên thẻ chỉ nói mình không áp dụng được và
 * đứng yên. Vì vậy lỗi từ endpoint này không đi qua `Toast`.
 */
export function PrioritySchoolComparison({
	filters,
	refreshToken = 0,
}: PrioritySchoolComparisonProps) {
	// `undefined` = lần tải đầu chưa xong; giữ số cũ khi refetch thay vì rơi về
	// skeleton mỗi lần đổi bộ lọc (CLAUDE.md §1).
	const [stats, setStats] = useState<PriorityHighSchoolStats | undefined>(undefined);
	const [unavailable, setUnavailable] = useState<string | null>(null);

	const cohort = filters?.admissionYear;
	const appliesToCohort = cohort === undefined || cohort === "2026";

	useEffect(() => {
		if (!appliesToCohort) {
			setUnavailable("Danh sách 149 trường ưu tiên chỉ áp dụng cho khoá 2026.");
			return;
		}
		let cancelled = false;
		statisticsAPIService
			.getPriorityHighSchools(toAPIFilter(filters, "total"))
			.then((data) => {
				if (cancelled) return;
				setStats(data);
				setUnavailable(null);
			})
			.catch((err) => {
				if (cancelled) return;
				console.error("Error fetching priority high school statistics:", err);
				setUnavailable("Không tải được số liệu nhóm trường ưu tiên.");
			});
		return () => {
			cancelled = true;
		};
	}, [filters, refreshToken, appliesToCohort]);

	if (unavailable) {
		return (
			<div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
				{unavailable}
			</div>
		);
	}

	if (stats === undefined) {
		return (
			<div className="rounded-lg border bg-card p-4 space-y-3">
				<Skeleton className="h-5 w-64" />
				<div className="grid gap-3 sm:grid-cols-2">
					<Skeleton className="h-20 w-full" />
					<Skeleton className="h-20 w-full" />
				</div>
			</div>
		);
	}

	const total = stats.inList.total + stats.notInList.total;
	const share = total > 0 ? (stats.inList.total / total) * 100 : 0;
	const gap = stats.inList.receivedRate - stats.notInList.receivedRate;

	return (
		<div className="rounded-lg border bg-card p-4 space-y-3">
			<div>
				<h3 className="text-sm font-medium">Trường THPT được ĐHQG-HCM ưu tiên</h3>
				<p className="text-xs text-muted-foreground">
					{formatCount(stats.inList.total)} / {formatCount(total)} sinh viên ({share.toFixed(1)}%)
					đến từ {formatCount(stats.matchedSchools)} trường trong danh sách 149 trường.
				</p>
			</div>

			<div className="grid gap-3 sm:grid-cols-2">
				<div className="rounded-md border p-3">
					<div className="text-xs text-muted-foreground">Thuộc danh sách ưu tiên</div>
					<div className="text-2xl font-semibold tabular-nums">
						{stats.inList.receivedRate.toFixed(1)}%
					</div>
					<div className="text-xs text-muted-foreground">
						{formatCount(stats.inList.received)} / {formatCount(stats.inList.total)} đã tiếp nhận
					</div>
				</div>
				<div className="rounded-md border p-3">
					<div className="text-xs text-muted-foreground">Ngoài danh sách</div>
					<div className="text-2xl font-semibold tabular-nums">
						{stats.notInList.receivedRate.toFixed(1)}%
					</div>
					<div className="text-xs text-muted-foreground">
						{formatCount(stats.notInList.received)} / {formatCount(stats.notInList.total)} đã tiếp
						nhận
					</div>
				</div>
			</div>

			{/* Chênh lệch là điều đáng đọc, nhưng nó KHÔNG chứng minh trường ưu tiên
			    khiến sinh viên nhập học nhiều hơn — hai nhóm còn khác nhau ở ngành,
			    điểm và địa bàn. Nêu con số, không kết luận nhân quả. */}
			<p className="text-xs text-muted-foreground">
				Chênh lệch tỉ lệ tiếp nhận: {gap >= 0 ? "+" : ""}
				{gap.toFixed(1)} điểm phần trăm. Đây là quan sát, không phải quan hệ nhân quả — hai nhóm còn
				khác nhau về ngành, điểm và địa bàn.
			</p>
		</div>
	);
}
